import { reject } from 'lodash';
import moment from 'moment';
import { hash } from 'components/timeline_control/helpers';
class Resource {
  constructor(
    label,
    data,
    timelineType,
    resourceTree,
    progenitorIndex,
    // Three functions. getBarLabelHtmlWidth, getLimitedHtmlWidth are
    // used to calculate the width of DOM elements. And then
    // getRelatedRecordIconWidth.
    widthCalculators
  ) {
    this.progenitorIndex = progenitorIndex;
    this.label = label;
    this.unique_key = data.unique_key || this.key();
    this.isLastColumn = typeof data.children === 'undefined';
    this.widthCalculators = widthCalculators;

    if (data.color) {
      this.color = data.color;
    }
    if (this.isLastColumn) {
      this.rows =
        timelineType === 'milestone'
          ? data.rows.filter(row => row.from)
          : data.rows;
      resourceTree.addRows(this.rows);
    } else {
      this.children = [];
      this.createChildrenNodes(
        data.children,
        timelineType,
        resourceTree,
        progenitorIndex
      );
    }
    this.sizeText();
  }

  static initClass() {
    this.prototype.groupedRows = [];
  }

  createChildrenNodes(children, timelineType, resourceTree, progenitorIndex) {
    children.forEach(data => {
      this.children.push(
        new Resource(
          data.label,
          data,
          timelineType,
          resourceTree,
          progenitorIndex,
          this.widthCalculators
        )
      );
    });
  }

  key() {
    return hash(this.label);
  }

  getRows(minDate, maxDate) {
    return reject(
      this.rows,
      row =>
        (minDate && moment.utc(minDate).isAfter(moment.utc(row.to))) ||
        (maxDate && moment.utc(maxDate).isBefore(moment.utc(row.from)))
    );
  }

  // If compact rows is on, we group together records into a single row if the
  // bars and their labels would not overlap.
  groupRows(scale, thickBars, isMilestone = false, showFloatingLabels = true) {
    this.groupedRows = [];
    const leftPositions = [];

    const totalWidth = scale.totalPixelWidth();
    const rows = Array.from(this.getRows(scale.minDate, scale.maxDate));
    const originalPositions = rows.reduce(
      (acc, row, index) => acc.set(row, index),
      new Map()
    );

    const sortedRows = rows.sort(
      (a, b) =>
        moment.utc(a.from) - moment.utc(b.from) ||
        originalPositions.get(a) - originalPositions.get(b) ||
        a.label.localeCompare(b.label)
    );

    for (const row of sortedRows) {
      let right;
      let left = scale.dateToOffset(moment.utc(row.from).clone());
      if (isMilestone) {
        right = left + 20;
      } else {
        right = scale.dateToOffsetEnd(
          moment.utc(row.to || moment.utc(row.from).add(9999, 'days')).clone()
        );
      }
      // Add space for the width of the related record opener icon which is
      // not included in the label
      const textWidth =
        this.widthCalculators.getBarLabelHtmlWidth(row.label).width +
        this.widthCalculators.getRelatedRecordIconWidth();
      const barWidth = Math.max(0, right - left);
      let rowAndLabelRight = left + barWidth + textWidth;

      // If we are not allowing labels to move as you scroll, just consider the max
      // of the bar width or text width to prevent labels overlapping in thin bars case
      // milestones are correctly calculated above as they will always be outside the milestone
      if (!showFloatingLabels && !isMilestone) {
        rowAndLabelRight = left + Math.max(barWidth, textWidth);
      }
      if (rowAndLabelRight > totalWidth) {
        // we need to place the label to the left side of the bar
        left -= textWidth;
      }

      let didCompactRow = false;
      for (let index = 0; index < leftPositions.length; index++) {
        // Allow equal positions to compact since this means that they picked 2 days beside each other
        const savedLeftPosition = leftPositions[index];
        if (left >= savedLeftPosition) {
          if (this.groupedRows[index] == null) {
            this.groupedRows[index] = [];
          }
          this.groupedRows[index].push(row);
          leftPositions[index] = rowAndLabelRight;
          didCompactRow = true;
          break;
        }
      }

      if (!didCompactRow) {
        if (this.groupedRows[leftPositions.length] == null) {
          this.groupedRows[leftPositions.length] = [];
        }
        this.groupedRows[leftPositions.length].push(row);
        leftPositions.push(rowAndLabelRight);
      }
    }

    return this.groupedRows;
  }

  totalDepth() {
    if (!this.isLastColumn && this.children.length > 0) {
      return this.children[0].totalDepth() + 1;
    } else {
      return 1;
    }
  }

  sizeText() {
    // This is not actually rendering the exact same thing the final product.
    // It is missing the <span class="name"> inner wrapper. Ensure that span
    // is not changing the size at all or else these calculations will be
    // incorrect.
    const textWidth = this.widthCalculators.getLimitedHtmlWidth(this.label);

    // each cell gets 2px of padding top and bottom, so we must add 4px to the total
    if (textWidth.height > 16) {
      return (this.height = Math.ceil(textWidth.height / 16.0) * 16.0 + 4);
    } else {
      return (this.height = 16 + 4);
    }
  }

  heightOfRows(
    compact,
    parentHeight = 0,
    minDate,
    maxDate,
    thickBars,
    fontSize
  ) {
    if (this.isLastColumn) {
      const rowChoice = compact
        ? this.groupedRows
        : this.getRows(minDate, maxDate);

      // No extra padding or height required for 12px font. Each size up needs
      // a little bit more padding between rows.
      const heightForFont = (fontSize - 12) * 1.5; // Timeline-row-height calculation
      const heightForMargin = (fontSize - 12) / 1.2; // Timeline-row-height-margin calculation
      // 36 is the row height. Needs to be 44 for thick bar height
      const rowHeight = (thickBars ? 44 : 36) + heightForFont + heightForMargin;
      return Math.max(
        Math.max(rowChoice.length, 1) * rowHeight + 10,
        this.height,
        parentHeight
      );
    } else {
      const heightByChild = this.height / this.children.length;

      return this.children.reduce((height, child) => {
        return (
          height +
          child.heightOfRows(
            compact,
            Math.max(heightByChild, parentHeight),
            minDate,
            maxDate,
            thickBars,
            fontSize
          )
        );
      }, 0);
    }
  }
}
Resource.initClass();

export default Resource;
